import React, { useEffect } from "react";
import clsx from "classnames";
import { useNotificationStore } from "@/zustand/useNotificationStore";
import { createPortal } from "react-dom";

interface ToastNotificationProps {
  position?: "bottom-center" | "top-right";
  time?: number;
  variant?: "green" | "blue" | "black" | "pink";
  source?: string;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  position = "bottom-center",
  time = 2000,
  variant = "green",
  source,
}) => {
  const {
    status,
    message,
    source: currentSource,
    clearNotification,
    notifications,
    removeNotification,
  } = useNotificationStore();
  const [showNotification, setShowNotification] = React.useState(
    !!status && currentSource === source
  );
  const [isSlideOut, setIsSlideOut] = React.useState(false);
  const [isSlideIn, setIsSlideIn] = React.useState(false);

  useEffect(() => {
    if (status && currentSource === source) {
      setShowNotification(true);
      setIsSlideOut(false);
      setIsSlideIn(false);

      // Start slide-in animation after a brief delay
      setTimeout(() => {
        setIsSlideIn(true);
      }, 50);

      // Get duration from store notification if available, fallback to hardcoded values
      const currentNotification = notifications.find(
        (n) => n.source === source && n.status === status
      );
      const autoCloseTime =
        currentNotification?.duration || (status === "error" ? 10000 : time);
      const timer = setTimeout(() => {
        // Start slide-out animation
        setIsSlideOut(true);
        setIsSlideIn(false);
        // Wait for animation to complete before hiding
        setTimeout(() => {
          setShowNotification(false);
          clearNotification();
          // Also remove from enhanced notifications array
          if (currentNotification) {
            removeNotification(currentNotification.id);
          }
          setIsSlideOut(false);
        }, 300); // Match CSS transition duration
      }, autoCloseTime);

      return () => clearTimeout(timer);
    }
  }, [status, currentSource, source, clearNotification, time]);

  if (!showNotification || !status || !message) return null;

  const containerBackgroundColors: Record<string, string> = {
    green: "#E2F5DF",
    blue: "#E0F6FF",
    black: "#E9EDF5",
    error: "#FFE1E2",
    pink: "#F8E9FF",
  };

  const textColors: Record<string, string> = {
    green: "#054016",
    blue: "#0F4184",
    black: "#121927",
    error: "#9E0015",
    pink: "#6D0056",
  };

  const buttonBackgroundColors: Record<string, string> = {
    green: "#06832B",
    blue: "#125DBE",
    black: "#293444",
    error: "#D31D35", // Error button background
    pink: "#9A0079",
  };

  const styles = {
    container: `flex items-center gap-4 justify-between w-[450px] pl-[24px] py-3 pr-[10px] rounded-[12px]`,
    message: textColors[status === "error" ? "error" : variant] || "#054016",
  };

  const handleClose = () => {
    console.log("clicked");
    setIsSlideOut(true);
    setIsSlideIn(false);
    setTimeout(() => {
      setShowNotification(false);
      clearNotification();
      // Also remove from enhanced notifications array
      const currentNotification = notifications.find(
        (n) => n.source === source && n.status === status
      );
      if (currentNotification) {
        removeNotification(currentNotification.id);
      }
      setIsSlideOut(false);
    }, 300); // Match CSS transition duration
  };

  const positionClasses = clsx({
    "fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 pointer-events-auto":
      position === "bottom-center",
    "fixed top-8 z-50 pointer-events-auto": position === "top-right",
  });

  const topRightPositioning =
    position === "top-right"
      ? {
          right: isSlideIn && !isSlideOut ? "32px" : "-450px", // Show at right-8 (32px) or hide off-screen
        }
      : {};

  const animationClasses = clsx(
    position === "top-right"
      ? "transition-all duration-300 ease-in-out"
      : "transition-transform duration-300 ease-in-out",
    position === "bottom-center" && {
      "transform translate-x-0": isSlideIn && !isSlideOut, // Slide in (visible)
      "transform translate-x-full": !isSlideIn || isSlideOut, // Initial state or slide out
    }
  );
  const toastElement = (
    <div
      className={positionClasses}
      style={{
        ...topRightPositioning,
      }}>
      <div
        className={`${styles.container} ${animationClasses}`}
        style={{
          backgroundColor:
            status === "error"
              ? containerBackgroundColors["error"]
              : containerBackgroundColors[variant],
        }}>
        <p
          className={styles.message}
          style={{
            color:
              status === "error" ? textColors["error"] : textColors[variant],
          }}>
          {message}
        </p>
        <div
          className="w-[24px] h-[24px]  rounded-[6px] flex justify-center items-center cursor-pointer flex-shrink-0"
          style={{
            backgroundColor:
              status === "error"
                ? buttonBackgroundColors["error"]
                : buttonBackgroundColors[variant],
          }}
          onClick={handleClose}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.442 4.44199L4.44199 12.442L3.55811 11.5581L11.5581 3.55811L12.442 4.44199Z"
              fill={"#FCFCFC"}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.55801 4.44199L11.558 12.442L12.4419 11.5581L4.44189 3.55811L3.55801 4.44199Z"
              fill={"#FCFCFC"}
            />
          </svg>
        </div>
      </div>
    </div>
  );
  return createPortal(toastElement, document.body);
};

export default ToastNotification;
